import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Input,
    List,
    Row,
    Space,
    Spin,
    Statistic,
    Typography,
    message
} from 'antd';

import {
    AimOutlined,
    CarOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    SearchOutlined,
    SwapOutlined
} from '@ant-design/icons';

import {
    MapContainer,
    Marker,
    Polyline,
    TileLayer,
    Tooltip,
    useMap
} from 'react-leaflet';


import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import axios from 'axios';



const {
    Title,
    Text
} = Typography;

const markerIcon = new L.Icon({
    iconUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',

    iconRetinaUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',

    shadowUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',

    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const DEFAULT_CENTER = [
    51.7682,
    55.0969
];

const formatDuration =
    totalMinutes => {
        const minutes =
            Number(totalMinutes) ||
            0;

        const hours =
            Math.floor(
                minutes / 60
            );

        const remainingMinutes =
            minutes % 60;

        if (hours === 0) {
            return `${remainingMinutes} мин`;
        }

        if (
            remainingMinutes === 0
        ) {
            return `${hours} ч`;
        }

        return (
            `${hours} ч ` +
            `${remainingMinutes} мин`
        );
    };

const createLatLng =
    point => [
        Number(point.latitude),
        Number(point.longitude)
    ];

const RouteMapController = ({
    from,
    to,
    routePositions
}) => {
    const map = useMap();

    useEffect(() => {
        const positions = [];

        if (from) {
            positions.push(
                createLatLng(from)
            );
        }

        if (to) {
            positions.push(
                createLatLng(to)
            );
        }

        if (
            routePositions.length >
            0
        ) {
            positions.push(
                ...routePositions
            );
        }

        if (positions.length === 0) {
            map.setView(
                DEFAULT_CENTER,
                7
            );

            return;
        }

        if (positions.length === 1) {
            map.setView(
                positions[0],
                14
            );

            return;
        }

        map.fitBounds(
            L.latLngBounds(
                positions
            ),
            {
                padding: [40, 40]
            }
        );
    }, [
        from,
        to,
        routePositions,
        map
    ]);

    return null;
};

const AddressSelector = ({
    title,
    placeholder,
    value,
    selectedPoint,
    results,
    loading,
    onChange,
    onSearch,
    onSelect
}) => {
    const handleKeyDown =
        event => {
            if (
                event.key ===
                'Enter'
            ) {
                event.preventDefault();
                onSearch();
            }
        };

    return (
        <Card
            size="small"
            title={
                <Space>
                    <EnvironmentOutlined />

                    <span>{title}</span>
                </Space>
            }
        >
            <Space.Compact
                style={{
                    width: '100%'
                }}
            >
                <Input
                    value={value}
                    placeholder={
                        placeholder
                    }
                    allowClear
                    onChange={
                        event =>
                            onChange(
                                event.target
                                    .value
                            )
                    }
                    onKeyDown={
                        handleKeyDown
                    }
                />

                <Button
                    type="primary"
                    icon={
                        <SearchOutlined />
                    }
                    loading={loading}
                    onClick={onSearch}
                >
                    Найти
                </Button>
            </Space.Compact>

            {selectedPoint && (
                <Alert
                    style={{
                        marginTop: 12
                    }}
                    type="success"
                    showIcon
                    message="Точка выбрана"
                    description={
                        selectedPoint.label
                    }
                />
            )}

            {results.length > 0 && (
                <List
                    style={{
                        marginTop: 12,
                        maxHeight: 260,
                        overflowY:
                            'auto'
                    }}
                    bordered
                    size="small"
                    dataSource={
                        results
                    }
                    renderItem={
                        item => (
                            <List.Item
                                style={{
                                    cursor:
                                        'pointer'
                                }}
                                onClick={() =>
                                    onSelect(
                                        item
                                    )
                                }
                                actions={[
                                    <Button
                                        key={
                                            item.id
                                        }
                                        type="link"
                                        size="small"
                                    >
                                        Выбрать
                                    </Button>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <AimOutlined />
                                    }
                                    title={
                                        item.label
                                    }
                                />
                            </List.Item>
                        )
                    }
                />
            )}

            {!loading &&
                value.trim()
                    .length >= 3 &&
                results.length ===
                    0 &&
                !selectedPoint && (
                    <Empty
                        style={{
                            marginTop: 16
                        }}
                        image={
                            Empty.PRESENTED_IMAGE_SIMPLE
                        }
                        description="Нажмите «Найти»"
                    />
                )}
        </Card>
    );
};

const RouteCalculatorPage =
    () => {
        const [
            fromQuery,
            setFromQuery
        ] = useState('');

        const [
            toQuery,
            setToQuery
        ] = useState('');

        const [
            fromPoint,
            setFromPoint
        ] = useState(null);

        const [
            toPoint,
            setToPoint
        ] = useState(null);

        const [
            fromResults,
            setFromResults
        ] = useState([]);

        const [
            toResults,
            setToResults
        ] = useState([]);

        const [
            fromLoading,
            setFromLoading
        ] = useState(false);

        const [
            toLoading,
            setToLoading
        ] = useState(false);

        const [
            routeLoading,
            setRouteLoading
        ] = useState(false);

        const [
            route,
            setRoute
        ] = useState(null);

        const yandexMapUrl =
    fromPoint && toPoint
        ? `https://yandex.ru/map-widget/v1/?rtext=${
              fromPoint.latitude
          },${
              fromPoint.longitude
          }~${
              toPoint.latitude
          },${
              toPoint.longitude
          }&rtt=auto`
        : `https://yandex.ru/map-widget/v1/?ll=${
              DEFAULT_CENTER[1]
          }%2C${
              DEFAULT_CENTER[0]
          }&z=7`;

        const searchAddress =
            async (
                query,
                setLoading,
                setResults
            ) => {
                const normalized =
                    query.trim();

                if (
                    normalized.length <
                    3
                ) {
                    message.warning(
                        'Введите минимум 3 символа адреса'
                    );

                    return;
                }

                setLoading(true);
                setResults([]);

                try {
                    const response =
                        await axios.get(
                            '/api/routes/search',
                            {
                                params: {
                                    q: normalized
                                }
                            }
                        );

                    const items =
                        response.data
                            ?.results ||
                        [];

                    setResults(items);

                    if (
                        items.length ===
                        0
                    ) {
                        message.info(
                            'Адрес не найден. Уточните город, улицу и номер дома'
                        );
                    }
                } catch (error) {
                    message.error(
                        error.response
                            ?.data
                            ?.message ||
                            'Ошибка поиска адреса'
                    );
                } finally {
                    setLoading(false);
                }
            };

        const handleFromChange =
            value => {
                setFromQuery(value);
                setFromPoint(null);
                setFromResults([]);
                setRoute(null);
            };

        const handleToChange =
            value => {
                setToQuery(value);
                setToPoint(null);
                setToResults([]);
                setRoute(null);
            };

        const selectFromPoint =
            point => {
                setFromPoint(point);
                setFromQuery(
                    point.label
                );
                setFromResults([]);
                setRoute(null);
            };

        const selectToPoint =
            point => {
                setToPoint(point);
                setToQuery(
                    point.label
                );
                setToResults([]);
                setRoute(null);
            };

        const swapPoints =
            () => {
                const previousFromQuery =
                    fromQuery;

                const previousFromPoint =
                    fromPoint;

                setFromQuery(toQuery);
                setFromPoint(toPoint);

                setToQuery(
                    previousFromQuery
                );

                setToPoint(
                    previousFromPoint
                );

                setFromResults([]);
                setToResults([]);
                setRoute(null);
            };

        const calculateRoute =
            async () => {
                if (
                    !fromPoint ||
                    !toPoint
                ) {
                    message.warning(
                        'Сначала выберите обе точки из результатов поиска'
                    );

                    return;
                }

                setRouteLoading(true);
                setRoute(null);

                try {
                    const response =
                        await axios.post(
                            '/api/routes/calculate',
                            {
                                from: {
                                    latitude:
                                        fromPoint.latitude,

                                    longitude:
                                        fromPoint.longitude
                                },

                                to: {
                                    latitude:
                                        toPoint.latitude,

                                    longitude:
                                        toPoint.longitude
                                }
                            }
                        );

                    setRoute(
                        response.data
                    );
                } catch (error) {
                    message.error(
                        error.response
                            ?.data
                            ?.message ||
                            'Не удалось построить маршрут'
                    );
                } finally {
                    setRouteLoading(false);
                }
            };

        const routePositions =
            useMemo(() => {
                const coordinates =
                    route?.geometry
                        ?.coordinates;

                if (
                    !Array.isArray(
                        coordinates
                    )
                ) {
                    return [];
                }

                return coordinates.map(
                    coordinate => [
                        Number(
                            coordinate[1]
                        ),
                        Number(
                            coordinate[0]
                        )
                    ]
                );
            }, [route]);

        return (
            <div
                style={{
                    padding: 24
                }}
            >
                <Title level={2}>
                    Расчёт маршрута
                </Title>

                <Text type="secondary">
                    Введите адреса, найдите
                    обе точки и рассчитайте
                    автомобильный маршрут.
                </Text>

                <Divider />

                <Row gutter={[16, 16]}>
                    <Col
                        xs={24}
                        lg={11}
                    >
                        <AddressSelector
                            title="Точка А"
                            placeholder="Например: Оренбург, Терешковой, 12"
                            value={
                                fromQuery
                            }
                            selectedPoint={
                                fromPoint
                            }
                            results={
                                fromResults
                            }
                            loading={
                                fromLoading
                            }
                            onChange={
                                handleFromChange
                            }
                            onSearch={() =>
                                searchAddress(
                                    fromQuery,
                                    setFromLoading,
                                    setFromResults
                                )
                            }
                            onSelect={
                                selectFromPoint
                            }
                        />
                    </Col>

                    <Col
                        xs={24}
                        lg={2}
                        style={{
                            display:
                                'flex',
                            justifyContent:
                                'center',
                            alignItems:
                                'center'
                        }}
                    >
                        <Button
                            shape="circle"
                            size="large"
                            icon={
                                <SwapOutlined />
                            }
                            disabled={
                                !fromQuery &&
                                !toQuery
                            }
                            onClick={
                                swapPoints
                            }
                            title="Поменять точки местами"
                        />
                    </Col>

                    <Col
                        xs={24}
                        lg={11}
                    >
                        <AddressSelector
                            title="Точка Б"
                            placeholder="Например: Орск, проспект Ленина, 5"
                            value={toQuery}
                            selectedPoint={
                                toPoint
                            }
                            results={
                                toResults
                            }
                            loading={
                                toLoading
                            }
                            onChange={
                                handleToChange
                            }
                            onSearch={() =>
                                searchAddress(
                                    toQuery,
                                    setToLoading,
                                    setToResults
                                )
                            }
                            onSelect={
                                selectToPoint
                            }
                        />
                    </Col>
                </Row>

                <div
                    style={{
                        marginTop: 20,
                        textAlign:
                            'center'
                    }}
                >
                    <Button
                        type="primary"
                        size="large"
                        icon={
                            <CarOutlined />
                        }
                        loading={
                            routeLoading
                        }
                        disabled={
                            !fromPoint ||
                            !toPoint
                        }
                        onClick={
                            calculateRoute
                        }
                    >
                        Рассчитать маршрут
                    </Button>
                </div>

                {routeLoading && (
                    <div
                        style={{
                            padding: 40,
                            textAlign:
                                'center'
                        }}
                    >
                        <Spin
                            size="large"
                            tip="Строим маршрут..."
                        />
                    </div>
                )}

                {route && (
                    <>
                        <Row
                            gutter={[
                                16,
                                16
                            ]}
                            style={{
                                marginTop: 24
                            }}
                        >
                            <Col
                                xs={24}
                                md={12}
                            >
                                <Card>
                                    <Statistic
                                        title="Расстояние"
                                        value={
                                            route.distanceKm
                                        }
                                        precision={
                                            1
                                        }
                                        suffix="км"
                                        prefix={
                                            <CarOutlined />
                                        }
                                    />
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Card>
                                    <Statistic
                                        title="Примерное время в пути"
                                        value={
                                            formatDuration(
                                                route.durationMinutes
                                            )
                                        }
                                        prefix={
                                            <ClockCircleOutlined />
                                        }
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row
    gutter={[16, 16]}
    style={{
        marginTop: 16
    }}
>
    <Col
        xs={24}
        xl={12}
    >
        <Card
            title="Маршрут OpenStreetMap"
            bodyStyle={{
                padding: 0,
                overflow: 'hidden'
            }}
        >
            <MapContainer
                center={DEFAULT_CENTER}
                zoom={7}
                style={{
                    width: '100%',
                    height: 350
                }}
            >
                <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />

                {fromPoint && (
                    <Marker
                        position={
                            createLatLng(
                                fromPoint
                            )
                        }
                        icon={markerIcon}
                    >
                        <Tooltip>
                            Точка А:{' '}
                            {fromPoint.label}
                        </Tooltip>
                    </Marker>
                )}

                {toPoint && (
                    <Marker
                        position={
                            createLatLng(
                                toPoint
                            )
                        }
                        icon={markerIcon}
                    >
                        <Tooltip>
                            Точка Б:{' '}
                            {toPoint.label}
                        </Tooltip>
                    </Marker>
                )}

                {routePositions.length > 0 && (
                    <Polyline
                        positions={
                            routePositions
                        }
                        pathOptions={{
                            color: '#1677ff',
                            weight: 6,
                            opacity: 0.8
                        }}
                    />
                )}

                <RouteMapController
                    from={fromPoint}
                    to={toPoint}
                    routePositions={
                        routePositions
                    }
                />
            </MapContainer>
        </Card>
    </Col>

    <Col
        xs={24}
        xl={12}
    >
        <Card
            title="Маршрут на Яндекс Картах"
            bodyStyle={{
                padding: 0,
                overflow: 'hidden'
            }}
        >
            <iframe
                key={yandexMapUrl}
                src={yandexMapUrl}
                title="Яндекс Карты"
                width="100%"
                height="350"
                frameBorder="0"
                allowFullScreen
                style={{
                    display: 'block',
                    border: 0
                }}
            />
        </Card>
    </Col>
</Row>

                        <Alert
                            style={{
                                marginTop: 16
                            }}
                            type="info"
                            showIcon
                            message="Время является ориентировочным"
                            description="Бесплатный OSRM рассчитывает время по дорожной сети, но не учитывает текущие пробки, ремонты и погодные условия."
                        />
                    </>
                )}
            </div>
        );
    };

export default RouteCalculatorPage;